import { useAuth0 } from '@auth0/auth0-react'
import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'

import { useGetToken } from './auth/useGetToken'
import { config } from './config'
import { createClient } from './data/graphql'
import { fetchMe } from './data/queries'
import { embedded, useEmbedStore } from './embed'
import { useAppStore } from './state/store'
import { GameView } from './ui/GameView'
import { Loading } from './ui/Loading'
import { LoginScreen } from './ui/LoginScreen'
import { OrgSelect } from './ui/OrgSelect'

export function App() {
  const { isAuthenticated, isLoading: authLoading } = useAuth0()
  const embedToken = useEmbedStore((s) => s.token)
  const embedOrgId = useEmbedStore((s) => s.organizationId)
  const authed = embedded
    ? Boolean(embedToken)
    : Boolean(config.devAuthToken) || isAuthenticated
  const getToken = useGetToken()

  const me = useAppStore((s) => s.me)
  const setMe = useAppStore((s) => s.setMe)
  const selectedOrgId = useAppStore((s) => s.selectedOrgId)
  const selectOrg = useAppStore((s) => s.selectOrg)

  const meQuery = useQuery({
    queryKey: ['me'],
    enabled: authed,
    queryFn: async () => fetchMe(createClient(await getToken())),
  })

  useEffect(() => {
    if (meQuery.data) setMe(meQuery.data)
  }, [meQuery.data, setMe])

  // Embedded: the dashboard's org is authoritative — follow it, including
  // live switches, so the office always shows the workspace the user is in.
  useEffect(() => {
    if (embedded && embedOrgId && embedOrgId !== selectedOrgId) {
      selectOrg(embedOrgId)
    }
  }, [embedOrgId, selectedOrgId, selectOrg])

  // Skip the picker when there is exactly one org to choose.
  useEffect(() => {
    if (me && !selectedOrgId && me.organizations.length === 1) {
      selectOrg(me.organizations[0].id)
    }
  }, [me, selectedOrgId, selectOrg])

  // Drop a persisted org id that isn't valid for this account/environment (e.g. a
  // staging org id carried into a prod session, or a since-removed org) so the
  // picker reappears instead of querying with an invalid organizationId.
  // Skipped while embedded: the parent's org is authoritative there, and this
  // clearing would fight the follow-the-dashboard effect above.
  useEffect(() => {
    if (embedded) return
    if (me && selectedOrgId && !me.organizations.some((o) => o.id === selectedOrgId)) {
      selectOrg(null)
    }
  }, [me, selectedOrgId, selectOrg])

  if (embedded) {
    // No LoginScreen / OrgSelect in the dashboard tab — identity and workspace
    // arrive from the parent, so every pre-game state is a quiet loader.
    if (!embedToken) return <Loading label="Connecting to your dashboard…" />
    if (meQuery.isLoading || !me) return <Loading label="Loading your workspaces…" />
    if (meQuery.isError)
      return <Loading label="Could not load your account. Check the console." />
    const orgId = embedOrgId ?? selectedOrgId
    if (!orgId) return <Loading label="Waiting for your workspace…" />
    return <GameView organizationId={orgId} />
  }

  if (authLoading && !config.devAuthToken) return <Loading label="Connecting…" />
  if (!authed) return <LoginScreen />
  if (meQuery.isLoading || !me) return <Loading label="Loading your workspaces…" />
  if (meQuery.isError)
    return <Loading label="Could not load your account. Check the console." />
  if (!selectedOrgId) return <OrgSelect />
  return <GameView organizationId={selectedOrgId} />
}
